import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
} from '@angular/core/rxjs-interop';
import { formatUserName } from '@app/helpers/formatName.helper';
import { UserAccount } from '@app/models/account.model';
import { UserService } from '@app/services/admin/user.service';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, finalize, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-project-wizard-contact-help',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './project-wizard-contact-help.component.html',
  styleUrl: './project-wizard-contact-help.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectWizardContactHelpComponent {
  contactUserId = input<string | null>(null);

  private readonly userService = inject(UserService);

  protected readonly contactUser = signal<UserAccount | null>(null);
  protected readonly loadingContact = signal(false);

  protected readonly contactName = computed(() => {
    const u = this.contactUser();
    if (!u) {
      return '-';
    }
    return formatUserName(u);
  });

  protected readonly phoneLine = computed(() => {
    const p = this.contactUser()?.phoneNumber?.trim();
    return p ? p : '-';
  });

  protected readonly emailLine = computed(() => {
    const e = this.contactUser()?.email?.trim();
    return e ? e : null;
  });

  constructor() {
    toObservable(this.contactUserId)
      .pipe(
        switchMap((id) => {
          this.contactUser.set(null);
          if (!id?.trim()) {
            this.loadingContact.set(false);
            return of(null);
          }
          this.loadingContact.set(true);
          return this.userService.getUserById(id.trim()).pipe(
            catchError(() => of(null)),
            finalize(() => this.loadingContact.set(false)),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((user) => this.contactUser.set(user));
  }
}
